import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import { ProgrammeIcon } from '../components/programmes/ProgrammeUI';
import { getProgramme } from '../data/programmeJourneys';
import {
  createProgrammeSession,
  getProgrammeApplication,
} from '../services/programmeJourneyService';
import '../styles/pages/ProgrammeJourneys.css';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ProgrammeLoginPage = ({ kind }) => {
  const programme = getProgramme(kind);
  const navigate = useNavigate();
  const application = getProgrammeApplication(programme.key);
  const [email, setEmail] = useState(application?.data?.email || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password.trim()) {
      setError('Enter an email address and password to continue.');
      return;
    }

    if (!emailPattern.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createProgrammeSession(programme.key, email.trim(), password);
      navigate(`/${programme.slug}/portal`);
    } catch (requestError) {
      setError(requestError.message || 'We could not sign you in. Check your details and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <SEO
        description={`Sign in to the ${programme.title} participant portal.`}
        title={`${programme.loginLabel} | Supernova Lab`}
      />

      <main
        className={`pj-login-page pj-theme--${programme.accent}`}
        data-programme={programme.key}
      >
        <div className="pj-login-shell">
          <section className="pj-login-hero" aria-labelledby="login-title">
            <h1 id="login-title">{programme.loginLabel}</h1>
            <p>
              {programme.key === 'zone'
                ? 'Track your application, complete onboarding and access your research credential after approval.'
                : 'Track your application and receive updates. Selected fellows can also access onboarding, cohort information and programme resources.'}
            </p>
          </section>

          <div className="pj-login-layout">
            <section className="pj-login-form-column">
              <div className="pj-login-panel__heading">
                <h2>{programme.loginLabel}</h2>
                <p>
                  Use the email and password connected to your programme
                  application.
                </p>
              </div>

              <section className="pj-login-panel" aria-label={programme.loginLabel}>
                <form noValidate onSubmit={handleSubmit}>
                  <div className="pj-field pj-field--wide">
                    <label htmlFor={`${programme.key}-login-email`}>
                      Email address
                    </label>
                    <input
                      autoComplete="email"
                      id={`${programme.key}-login-email`}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setError('');
                      }}
                      placeholder="Email address"
                      type="email"
                      value={email}
                    />
                  </div>

                  <div className="pj-field pj-field--wide">
                    <div className="pj-label-row">
                      <label htmlFor={`${programme.key}-login-password`}>
                        Password
                      </label>
                      <button
                        aria-controls={`${programme.key}-login-password`}
                        className="pj-password-toggle"
                        onClick={() => setShowPassword((current) => !current)}
                        type="button"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <input
                      autoComplete="current-password"
                      id={`${programme.key}-login-password`}
                      onChange={(event) => {
                        setPassword(event.target.value);
                        setError('');
                      }}
                      placeholder="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                    />
                    <Link
                      className="pj-inline-link pj-inline-link--field"
                      to={`/${programme.slug}/forgot-password`}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  {error ? (
                    <p className="pj-login-error" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <button className="pj-button pj-button--primary" disabled={isSubmitting} type="submit">
                    {isSubmitting ? 'Signing in…' : 'Sign in'}
                    <ProgrammeIcon name="arrow" size={18} />
                  </button>
                </form>
              </section>

              <p className="pj-login-support">
                Signing in for the first time?{' '}
                <Link to={`/${programme.slug}/access`}>
                  Set up portal access
                </Link>
              </p>
            </section>

            <aside className="pj-login-copy">
              <span aria-hidden="true">
                <ProgrammeIcon
                  name={programme.key === 'zone' ? 'flask' : 'spark'}
                  size={30}
                />
              </span>
              <h2>
                {programme.key === 'zone'
                  ? 'Your research system.'
                  : 'Your fellowship space.'}
              </h2>
              <p>
                {programme.key === 'zone'
                  ? 'Approved applicants will find their status, onboarding and research credential here.'
                  : 'Selected fellows will find cohort updates, onboarding and programme resources here.'}
              </p>
            </aside>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default ProgrammeLoginPage;
